import { useQuery, type QueryClient, type UseQueryOptions } from '@tanstack/react-query'
import { apiPaths } from '@/api/api-paths'
import { apiRequest, STABLE_QUERY_CACHE } from '@/api/query-client'
import { queryKeys } from '@/api/query-keys'

// created_by shape - matches API (Keycloak: username, firstName, lastName, email; or display_name)
export type UserSummary = {
  id: string
  username?: string
  firstName?: string
  lastName?: string
  email?: string
  display_name?: string
}

/** Display string for created_by (display_name, firstName+lastName, username, or email) */
export function getCreatedByDisplayName(createdBy: UserSummary | null): string {
  if (!createdBy) return '—'
  if (createdBy.display_name?.trim()) return createdBy.display_name.trim()
  const fullName = [createdBy.firstName, createdBy.lastName].filter(Boolean).join(' ').trim()
  return fullName || createdBy.username || createdBy.email || '—'
}

export type ProductSummary = {
  id: string
  name: string
}

/** Inputs shape returned by workflow runs list API (product may be absent for edge cases) */
export type WorkflowRunInputsSummary = {
  product?: ProductSummary
  lot_start_date?: string
  lot_end_date?: string
}

export type WorkflowRunConfigInputs = {
  workflow_run_id?: string
  user_id?: string
  organization_id?: string
  extra_config?: unknown
}

export type WorkflowRunDetailInputs = WorkflowRunInputsSummary & {
  products?: ProductSummary[]
  config?: WorkflowRunConfigInputs | null
  parameters?: string
}

/** @deprecated Prefer WorkflowRunDetailInputs for run detail payloads. */
export type WorkflowInputs = {
  products: ProductSummary[]
  lot_start_date: string
  lot_end_date: string
  config?: WorkflowRunConfigInputs | null
}

export type WorkflowOutputs = {
  excursions_detected: number | null
  reports: number | null
}

export type ExcursionTicketAssignee = {
  id: string
}

export type ExcursionTicket = {
  id: string
  excursion_number: number
  yield_percent: number
  wafers_impacted: number
  lots_impacted: number
  severity: string
  summary: string
  excursion_status: string
  assignee: ExcursionTicketAssignee | null
  data_source: string | null
}

export type WorkflowType = {
  id: string
  /** URL/catalog slug (hyphens), aligned with GET /workflows/workflow-types. */
  slug?: string
  name: string
  description: string | null
  /** When set, use for POST /workflow-runs `workflow_type` (Prefect deployment name). */
  prefect_deployment_name?: string | null
}

export type WorkflowTypeDetail = WorkflowType & {
  parameter_schema: Record<string, unknown>
  /** Agent/workflow version from backend (e.g. from Prefect deployment). */
  version?: string | number | null
}

export type WorkflowRunListItem = {
  id: string
  run_number: number | null
  workflow_type: string
  /** Display name from GET /workflow-runs (v2: `workflow_version_label`). */
  workflow_version_label?: string | null
  /** Alternate key used by some services; prefer `workflow_version_label` when both exist. */
  workflow_version_name?: string | null
  trigger_type: string
  status: string
  progress_percent: number | null
  created_by: UserSummary | null
  created_at: string
  excursion_events: ExcursionTicket[]
  inputs: WorkflowRunInputsSummary | null
  started_at: string | null
  completed_at: string | null
}

export type WorkflowRunReportSummary = {
  id: string
  title: string | null
  version: number | null
  created_at: string
  pdf_link: string
  word_link: string
}

export type WorkflowRunAction = {
  action: 'rerun' | 'schedule'
  enabled: boolean
}

export type WorkflowRunDetail = {
  id: string
  run_number: number | null
  prefect_deployment_name?: string | null
  workflow_type: string
  workflow_version_id?: string | null
  workflow_version_label?: string | null
  workflow_version_name?: string | null
  trigger_type: string
  status: string
  status_message: string | null
  progress_percent: number | null
  error_message: string | null
  outcome?: string | null
  created_by: UserSummary | null
  inputs: WorkflowRunDetailInputs | null
  outputs: WorkflowOutputs | null
  excursion_events: ExcursionTicket[]
  reports: WorkflowRunReportSummary[]
  actions?: WorkflowRunAction[]
  created_at: string
  started_at: string | null
  completed_at: string | null
}

export type Pagination = {
  page: number
  page_size: number
  total_items: number
  total_pages: number
  has_next: boolean
  has_previous: boolean
}

export type WorkflowRunsResponse = {
  items: WorkflowRunListItem[]
  pagination: Pagination
}

export type WorkflowRunStats = {
  period: string
  total_runs: number
  by_status: Record<string, number>
  by_workflow_type: Record<string, number>
  by_trigger_type: Record<string, number>
  avg_duration_seconds: number
  success_rate: number
}

/** Log group from workflow run logs snapshot */
export type WorkflowLogGroup = {
  [key: string]: unknown
}

/** Response from GET /workflow-runs/{run_id}/logs */
export type WorkflowRunLogsSnapshot = {
  workflow_id?: string
  logs: WorkflowLogGroup[]
  first_log_entry_id: string | null
  last_log_entry_id: string | null
}

/** Created-by user shape returned when creating a workflow run */
export type WorkflowRunCreatedBy = UserSummary

export type CreateWorkflowRunResponse = {
  id: string
  workflow_type: string
  trigger_type: string
  status: string
  status_message: string | null
  progress_percent: number
  created_by: WorkflowRunCreatedBy | null
  inputs?: WorkflowInputs | WorkflowRunInputsSummary | null
  created_at: string
}

export type CancelledWorkflowRun = {
  id: string
  status: string
  status_message: string
  completed_at: string
}

export type ListWorkflowRunsFilters = {
  page?: number
  page_size?: number
  workflow_type?: string | null
  flow_type?: string[] | null
  status?: string[] | null
  trigger_type?: string[] | null
  product_id?: string[] | null
  created_by_user_id?: string[] | null
  created_after?: string | null
  created_before?: string | null
  sort_by?: string | null
  sort_order?: 'asc' | 'desc' | null
}

type ApiResponse<T> = {
  message: string
  data: T
}

const RUN_NUMBER_PATTERN = /^\d+$/

export function isRunNumberRef(runRef: string | null | undefined): runRef is string {
  return Boolean(runRef && RUN_NUMBER_PATTERN.test(runRef))
}

// Fetch functions
async function fetchWorkflowTypes(): Promise<WorkflowType[]> {
  const res = await apiRequest('GET', apiPaths.workflows.workflowTypes.base())
  const json: ApiResponse<WorkflowType[]> = await res.json()
  return json.data
}

async function fetchWorkflowTypeDetails(workflowType: string): Promise<WorkflowTypeDetail> {
  const res = await apiRequest('GET', apiPaths.workflows.workflowTypes.byType(workflowType))
  const json: ApiResponse<WorkflowTypeDetail> = await res.json()
  return json.data
}

async function fetchWorkflowRuns(filters?: ListWorkflowRunsFilters): Promise<WorkflowRunsResponse> {
  const params = new URLSearchParams()
  if (filters) {
    if (filters.page !== undefined) params.append('page', String(filters.page))
    if (filters.page_size !== undefined) params.append('page_size', String(filters.page_size))
    if (filters.workflow_type) params.append('workflow_type', filters.workflow_type)
    if (filters.flow_type) {
      filters.flow_type.forEach((t) => params.append('flow_type', t))
    }
    if (filters.status) {
      filters.status.forEach((s) => params.append('status', s))
    }
    if (filters.trigger_type) {
      filters.trigger_type.forEach((t) => params.append('trigger_type', t))
    }
    if (filters.product_id) {
      filters.product_id.forEach((p) => params.append('product_id', p))
    }
    if (filters.created_by_user_id) {
      filters.created_by_user_id.forEach((u) => params.append('created_by_user_id', u))
    }
    if (filters.created_after) params.append('created_after', filters.created_after)
    if (filters.created_before) params.append('created_before', filters.created_before)
    if (filters.sort_by) params.append('sort_by', filters.sort_by)
    if (filters.sort_order) params.append('sort_order', filters.sort_order)
  }

  const url = `${apiPaths.workflows.workflowRuns.base()}?${params.toString()}`
  const res = await apiRequest('GET', url)
  const json: ApiResponse<WorkflowRunsResponse> = await res.json()
  return json.data
}

const DEFAULT_WORKFLOW_RUNS_FILTERS: ListWorkflowRunsFilters = {
  page: 1,
  page_size: 20,
}

/** Prefetch workflow types and workflow runs history for immediate display after login. */
export function prefetchWorkflowData(queryClient: QueryClient): void {
  void queryClient.prefetchQuery({
    queryKey: queryKeys.workflows.workflowTypes.list(),
    queryFn: fetchWorkflowTypes,
  })
  void queryClient.prefetchQuery({
    queryKey: queryKeys.workflows.workflowRuns.list(DEFAULT_WORKFLOW_RUNS_FILTERS),
    queryFn: () => fetchWorkflowRuns(DEFAULT_WORKFLOW_RUNS_FILTERS),
  })
}

export async function fetchWorkflowRunDetails(runId: string): Promise<WorkflowRunDetail> {
  const res = await apiRequest('GET', apiPaths.workflows.workflowRuns.byId(runId))
  const json: ApiResponse<WorkflowRunDetail> = await res.json()
  return json.data
}

export async function fetchWorkflowRunDetailsByRunNumber(
  runNumber: number | string,
): Promise<WorkflowRunDetail> {
  // Accept number|string because call sites may pass route params or typed API values.
  const res = await apiRequest('GET', apiPaths.workflows.workflowRuns.byRunNumber(runNumber))
  const json: ApiResponse<WorkflowRunDetail> = await res.json()
  return json.data
}

export async function fetchWorkflowRunDetailsByRef(runRef: string): Promise<WorkflowRunDetail> {
  if (isRunNumberRef(runRef)) {
    return fetchWorkflowRunDetailsByRunNumber(runRef)
  }
  return fetchWorkflowRunDetails(runRef)
}

/** Poll run details every intervalMs until the endpoint returns successfully. */
export async function pollWorkflowRunUntilReady(
  runId: string,
  queryClient: QueryClient,
  options?: { intervalMs?: number; maxAttempts?: number },
): Promise<WorkflowRunDetail> {
  const { intervalMs = 2000, maxAttempts = 60 } = options ?? {}
  let lastError: Error | null = null
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const data = await fetchWorkflowRunDetails(runId)
      queryClient.setQueryData(queryKeys.workflows.workflowRuns.byId(runId), data)
      return data
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err))
      if (attempt < maxAttempts - 1) {
        await new Promise((r) => setTimeout(r, intervalMs))
      }
    }
  }
  throw lastError ?? new Error('Run details not available. Try opening from the runs list.')
}

async function fetchWorkflowStats(orgId: string, period?: string): Promise<WorkflowRunStats> {
  const params = new URLSearchParams()
  params.append('org_id', orgId)
  if (period) {
    params.append('period', period)
  }

  const url = `${apiPaths.workflows.workflowRuns.stats()}?${params.toString()}`
  const res = await apiRequest('GET', url)
  const json: ApiResponse<WorkflowRunStats> = await res.json()
  return json.data
}

export async function fetchWorkflowRunLogs(runId: string): Promise<WorkflowRunLogsSnapshot> {
  const res = await apiRequest('GET', apiPaths.workflows.workflowRuns.logs(runId), undefined, {
    skipErrorCheck: true,
  })
  if (!res.ok) {
    const text = (await res.text()) || res.statusText
    throw new Error(`${res.status}: ${text}`)
  }
  const json: ApiResponse<WorkflowRunLogsSnapshot> = await res.json()
  return json.data
}

export async function fetchWorkflowRunLogsByRunNumber(
  runNumber: number | string,
): Promise<WorkflowRunLogsSnapshot> {
  // Accept number|string because call sites may pass route params or typed API values.
  const res = await apiRequest(
    'GET',
    apiPaths.workflows.workflowRuns.logsByRunNumber(runNumber),
    undefined,
    { skipErrorCheck: true },
  )
  if (!res.ok) {
    const text = (await res.text()) || res.statusText
    throw new Error(`${res.status}: ${text}`)
  }
  const json: ApiResponse<WorkflowRunLogsSnapshot> = await res.json()
  return json.data
}

// Query hooks
export function useWorkflowTypes(
  options?: Omit<UseQueryOptions<WorkflowType[]>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: queryKeys.workflows.workflowTypes.list(),
    queryFn: fetchWorkflowTypes,
    ...STABLE_QUERY_CACHE,
    ...options,
  })
}

export function useWorkflowTypeDetails(
  workflowType: string,
  options?: Omit<UseQueryOptions<WorkflowTypeDetail>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: queryKeys.workflows.workflowTypes.byType(workflowType),
    queryFn: () => fetchWorkflowTypeDetails(workflowType),
    enabled: !!workflowType,
    ...STABLE_QUERY_CACHE,
    ...options,
  })
}

export function useWorkflowRuns(
  filters?: ListWorkflowRunsFilters,
  options?: Omit<UseQueryOptions<WorkflowRunsResponse>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: queryKeys.workflows.workflowRuns.list(filters),
    queryFn: () => fetchWorkflowRuns(filters),
    // Preserve previous page while fetching a new page/sort/filter.
    placeholderData: (prev) => prev,
    ...STABLE_QUERY_CACHE,
    ...options,
  })
}

export function useWorkflowRunDetails(
  runId: string,
  options?: Omit<UseQueryOptions<WorkflowRunDetail>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: queryKeys.workflows.workflowRuns.byId(runId),
    queryFn: () => fetchWorkflowRunDetails(runId),
    enabled: !!runId,
    ...options,
  })
}

export function useWorkflowRunDetailsByRef(
  runRef: string,
  options?: Omit<UseQueryOptions<WorkflowRunDetail>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: isRunNumberRef(runRef)
      ? queryKeys.workflows.workflowRuns.byRunNumber(runRef)
      : queryKeys.workflows.workflowRuns.byId(runRef),
    queryFn: () => fetchWorkflowRunDetailsByRef(runRef),
    enabled: !!runRef,
    ...options,
  })
}

export function useWorkflowStats(
  orgId: string,
  period?: string,
  options?: Omit<UseQueryOptions<WorkflowRunStats>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: queryKeys.workflows.workflowRuns.stats(orgId, period),
    queryFn: () => fetchWorkflowStats(orgId, period),
    ...options,
  })
}

/** Prefer `useRunTracesController` + `RunTracesProvider` on run detail so the logs query runs once. */
export function useWorkflowRunLogsSnapshot(
  runId: string,
  options?: Omit<UseQueryOptions<WorkflowRunLogsSnapshot>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: queryKeys.workflows.workflowRuns.logs(runId),
    queryFn: () => fetchWorkflowRunLogs(runId),
    enabled: !!runId,
    refetchOnMount: false,
    refetchOnReconnect: false,
    // Do not inherit global `retry: 2` — logs failures should not multiply requests.
    retry: false,
    ...options,
  })
}
